import { useMemo } from "react";
import { FormInputDraft } from "@/lib/form-input-draft";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function isSchemaRecord(
	value: unknown,
): value is Record<string, unknown> {
	return (
		typeof value === "object" &&
		value !== null &&
		!Array.isArray(value) &&
		!(value instanceof FormInputDraft)
	);
}

function formatArrayValue(value: unknown): string {
	return Array.isArray(value)
		? value.map((item) => String(item)).join(", ")
		: "";
}

function parseArrayValue(
	text: string,
	itemType: unknown,
	itemEnum?: unknown[],
	minItems?: number,
	maxItems?: number,
): { values?: unknown[]; error?: string } {
	const tokens = text
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);

	let values: unknown[];
	if (itemType === "number" || itemType === "integer") {
		const numericValues = tokens.map(Number);
		const valid = numericValues.every((value) =>
			itemType === "integer" ? Number.isInteger(value) : Number.isFinite(value),
		);
		if (!valid) {
			return {
				error: `Enter comma-separated ${
					itemType === "integer" ? "integers" : "numbers"
				}`,
			};
		}
		values = numericValues;
	} else if (itemType === "boolean") {
		if (tokens.some((token) => token !== "true" && token !== "false")) {
			return { error: "Enter comma-separated true or false values" };
		}
		values = tokens.map((token) => token === "true");
	} else {
		values = tokens;
	}

	if (
		itemEnum &&
		values.some(
			(value) =>
				!itemEnum.some((allowedValue) => Object.is(value, allowedValue)),
		)
	) {
		return { error: "Enter only the allowed values" };
	}
	if (minItems !== undefined && values.length < minItems) {
		return {
			error: `Enter at least ${minItems} ${minItems === 1 ? "item" : "items"}`,
		};
	}
	if (maxItems !== undefined && values.length > maxItems) {
		return {
			error: `Enter at most ${maxItems} ${maxItems === 1 ? "item" : "items"}`,
		};
	}

	return { values };
}

export function SchemaPrimitiveArrayInput({
	id,
	value,
	itemType,
	itemEnum,
	minItems,
	maxItems,
	ariaLabel,
	ariaDescribedBy,
	placeholder,
	readOnly = false,
	showHelp = true,
	onChange,
}: {
	id: string;
	value: unknown;
	itemType?: unknown;
	itemEnum?: unknown[];
	minItems?: number;
	maxItems?: number;
	ariaLabel?: string;
	ariaDescribedBy?: string;
	placeholder?: string;
	readOnly?: boolean;
	showHelp?: boolean;
	onChange: (value: unknown[] | FormInputDraft) => void;
}) {
	const draft =
		value instanceof FormInputDraft ? value.text : formatArrayValue(value);
	const error = value instanceof FormInputDraft ? value.error : null;

	return (
		<>
			<Input
				id={id}
				aria-label={ariaLabel}
				type="text"
				value={draft}
				onChange={(event) => {
					const nextDraft = event.target.value;
					const parsed = parseArrayValue(
						nextDraft,
						itemType,
						itemEnum,
						minItems,
						maxItems,
					);
					onChange(
						new FormInputDraft(
							"primitive-array",
							nextDraft,
							parsed.values,
							parsed.error ?? null,
						),
					);
				}}
				placeholder={placeholder || "Enter comma-separated values"}
				readOnly={readOnly}
				disabled={readOnly}
				aria-invalid={error ? true : undefined}
				aria-describedby={
					[
						error ? `${id}-error` : showHelp ? `${id}-help` : undefined,
						ariaDescribedBy,
					]
						.filter(Boolean)
						.join(" ") || undefined
				}
			/>
			{error ? (
				<p id={`${id}-error`} role="alert" className="text-xs text-destructive">
					{error}
				</p>
			) : showHelp ? (
				<p id={`${id}-help`} className="text-xs text-muted-foreground">
					Enter values separated by commas
				</p>
			) : null}
		</>
	);
}

function matchesArrayItemType(value: unknown, itemType: unknown): boolean {
	switch (itemType) {
		case "array":
			return Array.isArray(value);
		case "object":
			return isSchemaRecord(value);
		case "integer":
			return typeof value === "number" && Number.isInteger(value);
		case "number":
			return typeof value === "number" && Number.isFinite(value);
		case "boolean":
			return typeof value === "boolean";
		case "string":
			return typeof value === "string";
		default:
			return true;
	}
}

export function isArrayOfEditablePrimitiveItems(value: unknown): boolean {
	if (value instanceof FormInputDraft) return value.kind === "primitive-array";
	return (
		Array.isArray(value) &&
		value.every(
			(item) =>
				typeof item === "string" ||
				typeof item === "number" ||
				typeof item === "boolean",
		)
	);
}

function SchemaJsonContainerEditor({
	id,
	value,
	expectedType,
	itemType,
	itemEnum,
	minItems,
	maxItems,
	ariaLabel,
	ariaDescribedBy,
	readOnly = false,
	onChange,
}: {
	id: string;
	value: unknown;
	expectedType: "array" | "object";
	itemType?: unknown;
	itemEnum?: unknown[];
	minItems?: number;
	maxItems?: number;
	ariaLabel: string;
	ariaDescribedBy?: string;
	readOnly?: boolean;
	onChange: (value: FormInputDraft) => void;
}) {
	const draft = useMemo(() => {
		if (value instanceof FormInputDraft) return value.text;
		try {
			return JSON.stringify(value, null, 2) ?? "";
		} catch {
			return "";
		}
	}, [value]);
	const error = value instanceof FormInputDraft ? value.error : null;

	return (
		<>
			<Textarea
				id={id}
				aria-label={ariaLabel}
				value={draft}
				onChange={(event) => {
					const nextDraft = event.target.value;
					try {
						const parsed = JSON.parse(nextDraft) as unknown;
						const isExpectedType =
							expectedType === "array"
								? Array.isArray(parsed)
								: isSchemaRecord(parsed);
						if (!isExpectedType) {
							throw new Error(`Value must be a JSON ${expectedType}`);
						}
						if (
							expectedType === "array" &&
							(parsed as unknown[]).some(
								(item) => !matchesArrayItemType(item, itemType),
							)
						) {
							throw new Error(`Every item must be a JSON ${String(itemType)}`);
						}
						if (
							expectedType === "array" &&
							itemEnum &&
							(parsed as unknown[]).some(
								(item) =>
									!itemEnum.some((allowedItem) => Object.is(item, allowedItem)),
							)
						) {
							throw new Error("Enter only the allowed values");
						}
						if (
							expectedType === "array" &&
							minItems !== undefined &&
							(parsed as unknown[]).length < minItems
						) {
							throw new Error(
								`Enter at least ${minItems} ${minItems === 1 ? "item" : "items"}`,
							);
						}
						if (
							expectedType === "array" &&
							maxItems !== undefined &&
							(parsed as unknown[]).length > maxItems
						) {
							throw new Error(
								`Enter at most ${maxItems} ${maxItems === 1 ? "item" : "items"}`,
							);
						}
						onChange(
							new FormInputDraft(`json-${expectedType}`, nextDraft, parsed),
						);
					} catch (cause) {
						onChange(
							new FormInputDraft(
								`json-${expectedType}`,
								nextDraft,
								undefined,
								cause instanceof SyntaxError
									? `Enter a valid JSON ${expectedType}`
									: cause instanceof Error
										? cause.message
										: `Enter a valid JSON ${expectedType}`,
							),
						);
					}
				}}
				readOnly={readOnly}
				disabled={readOnly}
				aria-invalid={error ? true : undefined}
				aria-describedby={
					[error ? `${id}-error` : undefined, ariaDescribedBy]
						.filter(Boolean)
						.join(" ") || undefined
				}
				className="min-h-24 font-mono text-xs"
			/>
			{error ? (
				<p id={`${id}-error`} role="alert" className="text-xs text-destructive">
					{error}
				</p>
			) : null}
		</>
	);
}

export function SchemaJsonObjectEditor({
	id,
	value,
	ariaLabel = "JSON object",
	ariaDescribedBy,
	readOnly = false,
	onChange,
}: {
	id: string;
	value: unknown;
	ariaLabel?: string;
	ariaDescribedBy?: string;
	readOnly?: boolean;
	onChange: (value: Record<string, unknown> | FormInputDraft) => void;
}) {
	return (
		<SchemaJsonContainerEditor
			id={id}
			value={value}
			expectedType="object"
			ariaLabel={ariaLabel}
			ariaDescribedBy={ariaDescribedBy}
			readOnly={readOnly}
			onChange={onChange}
		/>
	);
}

export function SchemaJsonArrayEditor({
	id,
	value,
	ariaLabel = "JSON array",
	ariaDescribedBy,
	readOnly = false,
	itemType,
	itemEnum,
	minItems,
	maxItems,
	onChange,
}: {
	id: string;
	value: unknown;
	ariaLabel?: string;
	ariaDescribedBy?: string;
	readOnly?: boolean;
	itemType?: unknown;
	itemEnum?: unknown[];
	minItems?: number;
	maxItems?: number;
	onChange: (value: unknown[] | FormInputDraft) => void;
}) {
	return (
		<SchemaJsonContainerEditor
			id={id}
			value={value}
			expectedType="array"
			itemType={itemType}
			itemEnum={itemEnum}
			minItems={minItems}
			maxItems={maxItems}
			ariaLabel={ariaLabel}
			ariaDescribedBy={ariaDescribedBy}
			readOnly={readOnly}
			onChange={onChange}
		/>
	);
}

export function SchemaNumberInput({
	id,
	value,
	onChange,
	readOnly = false,
	integer = false,
	ariaLabel,
	ariaDescribedBy,
	placeholder,
}: {
	id: string;
	value: unknown;
	onChange: (value: FormInputDraft) => void;
	readOnly?: boolean;
	integer?: boolean;
	ariaLabel?: string;
	ariaDescribedBy?: string;
	placeholder?: string;
}) {
	const text =
		value instanceof FormInputDraft ? value.text : String(value ?? "");
	const error = value instanceof FormInputDraft ? value.error : null;
	return (
		<>
			<Input
				id={id}
				type="text"
				inputMode="decimal"
				aria-label={ariaLabel}
				value={text}
				readOnly={readOnly}
				disabled={readOnly}
				placeholder={placeholder}
				aria-invalid={error ? true : undefined}
				aria-describedby={
					[error ? `${id}-error` : undefined, ariaDescribedBy]
						.filter(Boolean)
						.join(" ") || undefined
				}
				onChange={(event) => {
					const next = event.target.value;
					const numeric = Number(next);
					const valid =
						/^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?$/.test(
							next.trim(),
						) &&
						Number.isFinite(numeric) &&
						(!integer || Number.isInteger(numeric));
					onChange(
						new FormInputDraft(
							"number",
							next,
							next.trim() !== "" && valid ? numeric : undefined,
							next.trim() === "" || valid
								? null
								: `Enter a valid ${integer ? "integer" : "number"}`,
						),
					);
				}}
			/>
			{error && (
				<p id={`${id}-error`} role="alert" className="text-xs text-destructive">
					{error}
				</p>
			)}
		</>
	);
}
